/**
 * 백엔드 공통 응답 envelope.
 *
 * 모든 service-area / order API는 동일 구조로 응답:
 *   - 정상: { txid, status, message, data }
 *   - 비정상(4xx/5xx): { txid, status, message, error: { code, description } }
 *
 * `apis/index.ts`의 response 인터셉터에서 unwrap 처리 — 호출 측은 `data`만 받음.
 * 비정상 status는 ApiError로 throw → React Query가 처리.
 */
export interface ApiResponse<T> {
  /** 요청 트랜잭션 ID (uuid v4) — Sentry/로그 추적용 */
  txid: string
  /** 상태 코드 (HTTP 상태 코드와 동일) */
  status: number
  /** 상태 메시지 ("성공" / 에러 메시지) */
  message: string
  /** 응답 본문 — 비정상 응답 시 null 가능 */
  data: T | null
  /** 에러 정보 — status >= 400일 때 존재. 4xx/5xx 분기에 사용 */
  error?: ApiErrorDetail
}

export interface ApiErrorDetail {
  /** 백엔드 에러 코드 (예: 'E422200', 'E409200', 'E422201') */
  code: string
  /** 에러 상세 설명 — null 가능 */
  description: string | null
}

/**
 * API 호출 실패 시 인터셉터에서 throw하는 에러 클래스.
 *
 * `code`는 백엔드 에러 코드 (E422200 등) — 호출자가 분기 처리에 사용.
 * 백엔드가 envelope 없이 응답하거나 code 누락 시엔 빈 문자열로 채움.
 *
 * gacha의 ApiError와 차이 — gacha는 `super(description ?? code)`로 자동 생성하지만
 * service-area는 백엔드 envelope의 `message` 필드(예: "서비스 가능 지역이 아닙니다")를
 * 사용자 친화적 한국어 카피로 활용하기 위해 별도 인자로 받음.
 * `description`은 추가 디버그 정보용으로 별도 보존.
 */
export class ApiError extends Error {
  readonly code: string
  readonly description: string | null
  readonly status: number
  readonly txid: string

  constructor(code: string, description: string | null, message: string, status: number, txid: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.description = description
    this.status = status
    this.txid = txid
  }
}
